import React, { useState } from 'react';
import { getPresignedUrl } from '../../../services/courseRunsService';
import {
  DesignDocument,
  DesignRun,
  SimonBlock,
  SimonCourseSetup,
  designRunsService,
} from '../../../services/designRunsService';
import { Chip } from './editors';

const NAVY = '#1a2332';

export function DetailPanel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl bg-white border border-gray-200 p-5">
      <h3 className="text-base font-semibold mb-3" style={{ color: NAVY }}>{title}</h3>
      {children}
    </div>
  );
}

/** Group blocks into sections client-side (mirrors backend section_slices). */
function sections(blocks: SimonBlock[]): Array<{ section: SimonBlock; slides: SimonBlock[] }> {
  const out: Array<{ section: SimonBlock; slides: SimonBlock[] }> = [];
  for (const b of blocks) {
    if (b.type === 'section') out.push({ section: b, slides: [] });
    else if (out.length) out[out.length - 1].slides.push(b);
  }
  return out;
}

// Labels as the Simon editor names its block types (mirrors compile.py)
const BLOCK_LABELS: Record<string, string> = {
  plain_text: 'Plain text', text_image: 'Text and image', video: 'Video',
  knowledge_check: 'Knowledge check', graded_quiz: 'Graded quiz', survey: 'Survey',
};

// Simon's empirical field limits and its character counting (markdown markup
// stripped) — both mirror shared/simon/schema.py
const SIMON_LIMITS = { title: 70, body: 400, question: 690, answer: 192, explanation: 500 };
const MD_MARKUP = /[*_`#>]|\[|\]\([^)]*\)/g;

function simonLen(text: string | undefined): number {
  return text ? text.replace(MD_MARKUP, '').length : 0;
}

function CharMeter({ text, limit }: { text: string | undefined; limit: number }) {
  const n = simonLen(text);
  const cls = n > limit ? 'text-red-600 font-semibold'
    : n > limit * 0.9 ? 'text-amber-600' : 'text-gray-300';
  return <span className={`text-[10px] font-mono shrink-0 ${cls}`}>{n}/{limit}</span>;
}

function WizardRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 text-sm border-b border-gray-50 pb-1.5">
      <span className="text-gray-400 shrink-0">{label}</span>
      <span className="text-gray-700 text-right">{children}</span>
    </div>
  );
}

/** What goes into Simon's course-creation wizard, field for field. */
function WizardCard({ course }: { course: SimonCourseSetup }) {
  const style = course.style ?? {};
  const settings = course.settings ?? {};
  return (
    <DetailPanel title="Course setup (creation wizard)">
      <div className="space-y-1.5">
        <WizardRow label="Title">
          <span className="inline-flex items-baseline gap-2">
            {course.title} <CharMeter text={course.title} limit={SIMON_LIMITS.title} />
          </span>
        </WizardRow>
        {course.subtitle && <WizardRow label="Subtitle">{course.subtitle}</WizardRow>}
        {course.description && <WizardRow label="Description">{course.description}</WizardRow>}
        {course.author && <WizardRow label="Author">{course.author}</WizardRow>}
        {(course.learning_objectives ?? []).map((o, i) => (
          <WizardRow key={i} label={`Learning objective ${i + 1}`}>{o}</WizardRow>
        ))}
        {style.publisher_logo && (
          <WizardRow label="Publisher logo"><span className="font-mono text-xs">{style.publisher_logo}</span></WizardRow>
        )}
        {style.cover_background && (
          <WizardRow label="Cover background"><span className="font-mono text-xs">{style.cover_background}</span></WizardRow>
        )}
        {style.theme && <WizardRow label="Course UI appearance">{style.theme}</WizardRow>}
        {style.background_music && (
          <WizardRow label="Background music"><span className="font-mono text-xs">{style.background_music}</span></WizardRow>
        )}
        <WizardRow label="Advance slides after voiceover">{settings.advance_after_voiceover ? 'On' : 'Off'}</WizardRow>
        <WizardRow label="Require sequential order">{settings.sequential_order ? 'On' : 'Off'}</WizardRow>
      </div>
    </DetailPanel>
  );
}

export function OutlineDetail({ doc }: { doc: DesignDocument | null }) {
  const courseDoc = doc?.realizations?.simon?.course_doc;
  const blocks = courseDoc?.blocks ?? [];
  if (!blocks.length) return <DetailPanel title="Course outline">Not outlined yet.</DetailPanel>;
  return (
    <div className="space-y-4">
      {courseDoc?.course && <WizardCard course={courseDoc.course} />}
      <DetailPanel title="Course outline — Simon sections & slides">
        <div className="space-y-2">
          {sections(blocks).map(({ section, slides }, i) => (
            <div key={i} className="flex justify-between text-sm text-gray-700 border-b border-gray-100 pb-1.5">
              <span className="font-medium">{section.title}</span>
              <span className="text-gray-400">
                {slides.length} slides · {slides.filter((s) => s.type === 'knowledge_check').length} checks
                {slides.some((s) => s.type === 'video') ? ' · video' : ''}
              </span>
            </div>
          ))}
        </div>
      </DetailPanel>
    </div>
  );
}

function SlideCard({ number, block }: { number: string; block: SimonBlock }) {
  return (
    <div className="border border-gray-200 rounded-lg bg-white overflow-hidden">
      <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 border-b border-gray-100">
        <span className="text-[11px] font-mono font-bold text-gray-500">SLIDE {number}</span>
        <Chip tone={block.type === 'knowledge_check' || block.type === 'graded_quiz' ? 'indigo'
          : block.type === 'video' ? 'blue' : 'gray'}>
          {BLOCK_LABELS[block.type] ?? block.type}
        </Chip>
        <span className="flex-1" />
        {block.voiceover?.mode === 'audio_file' && (
          <span className="text-[11px] text-gray-400">🎧 {block.voiceover.file}</span>
        )}
        {block.video && <span className="text-[11px] text-gray-400">🎬 {block.video}</span>}
        {block.image && <span className="text-[11px] text-gray-400">🖼 {block.image}</span>}
      </div>
      <div className="p-3 space-y-2">
        {block.title && (
          <div className="flex items-baseline justify-between gap-3">
            <div className="text-sm font-semibold text-gray-900">{block.title}</div>
            <CharMeter text={block.title} limit={SIMON_LIMITS.title} />
          </div>
        )}
        {block.body && (
          <div className="flex items-start justify-between gap-3">
            <div className="text-sm text-gray-700 whitespace-pre-wrap flex-1">{block.body}</div>
            <CharMeter text={block.body} limit={SIMON_LIMITS.body} />
          </div>
        )}
        {block.narration && (
          <div className="flex items-start justify-between gap-3 border-t border-gray-50 pt-1.5">
            <div className="text-xs text-gray-400 italic flex-1">🎤 {block.narration}</div>
            <CharMeter text={block.narration} limit={700} />
          </div>
        )}
        {(block.questions ?? []).map((q, i) => (
          <div key={i} className="border-t border-gray-100 pt-2">
            <div className="flex items-start justify-between gap-3">
              <div className="text-sm text-gray-800 italic flex-1">{q.text}</div>
              <CharMeter text={q.text} limit={SIMON_LIMITS.question} />
            </div>
            <ul className="mt-1 space-y-0.5">
              {q.answers.map((a, j) => (
                <li key={j} className="flex items-start justify-between gap-3 text-sm">
                  <span className={a.correct ? 'text-green-700' : 'text-gray-600'}>
                    {a.correct ? '✅' : '◻️'} {a.text}
                  </span>
                  <CharMeter text={a.text} limit={SIMON_LIMITS.answer} />
                </li>
              ))}
            </ul>
            {q.explanation && (
              <div className="mt-1 flex items-start justify-between gap-3">
                <div className="text-xs text-gray-400 flex-1">Explanation: {q.explanation}</div>
                <CharMeter text={q.explanation} limit={SIMON_LIMITS.explanation} />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/** A rough learner-facing approximation of the slide, as Simon presents it. */
function SlidePreview({ number, block }: { number: string; block: SimonBlock }) {
  return (
    <div className="rounded-xl border-2 shadow-sm overflow-hidden bg-white" style={{ borderColor: '#2d3748' }}>
      <div className="px-6 py-5 min-h-[140px] flex flex-col gap-3">
        {block.title && (
          <div className="text-lg font-bold text-center" style={{ color: NAVY }}>{block.title}</div>
        )}
        {block.type === 'video' && block.video && (
          <div className="flex items-center justify-center rounded-lg text-white text-sm py-10"
            style={{ backgroundColor: NAVY }}>
            ▶ {block.video}
          </div>
        )}
        {block.image && (
          <div className="flex items-center justify-center rounded-lg bg-gray-100 text-gray-400 text-xs py-10">
            🖼 {block.image}
          </div>
        )}
        {block.body && (
          <div className="text-sm text-gray-700 whitespace-pre-wrap text-center">{block.body}</div>
        )}
        {(block.questions ?? []).map((q, i) => (
          <div key={i}>
            <div className="text-sm font-medium text-gray-800 mb-1.5">{q.text}</div>
            <div className="space-y-1">
              {q.answers.map((a, j) => (
                <div key={j}
                  className={`px-3 py-1.5 rounded-md border text-sm ${a.correct ? 'border-green-300 bg-green-50 text-gray-800' : 'border-gray-200 text-gray-600'}`}>
                  {q.select === 'multiple' ? '☐' : '○'} {a.text}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="px-3 py-1 bg-gray-50 border-t border-gray-100 flex items-center gap-2 text-[10px] text-gray-400 font-mono">
        <span>SLIDE {number}</span>
        {block.voiceover?.mode === 'audio_file' && <span>· 🎧 {block.voiceover.file}</span>}
        {block.narration && <span>· 🎤 narrated</span>}
      </div>
    </div>
  );
}

/** The slide deck exactly as it imports into Simon: sections and slides
    numbered as in the build prompt. Copy view shows char counts vs Simon's
    limits; Preview view approximates the learner-facing slide. */
export function DraftingDetail({ doc }: { doc: DesignDocument | null }) {
  const [mode, setMode] = useState<'copy' | 'preview'>('copy');
  const blocks = doc?.realizations?.simon?.course_doc?.blocks ?? [];
  const trimFlags = doc?.realizations?.simon?.course_doc?.drafting_flags ?? [];
  if (!blocks.length) return <DetailPanel title="Slide deck">Nothing drafted yet.</DetailPanel>;
  const secs = sections(blocks);
  return (
    <DetailPanel title="Slide deck — what imports into Simon">
      {trimFlags.length > 0 && (
        <div className="mb-3 p-2.5 bg-amber-50 border border-amber-200 rounded-md text-sm">
          <div className="font-medium text-amber-800 mb-1">
            {trimFlags.length} slide{trimFlags.length > 1 ? 's' : ''} trimmed to fit Simon's limits — worth a look:
          </div>
          <ul className="text-amber-700 space-y-0.5">
            {trimFlags.map((f, i) => (
              <li key={i}>• Section {f.section}, block {f.index}: {f.reason}</li>
            ))}
          </ul>
        </div>
      )}
      {/* Section rail: jump to any section, mirroring Simon's editor rail */}
      <div className="sticky top-0 z-10 bg-white pb-2 mb-3 border-b border-gray-100 flex items-center gap-1.5 flex-wrap">
        {secs.map(({ section, slides }, i) => (
          <button key={i}
            className="px-2 py-1 rounded-md text-xs bg-gray-100 text-gray-600 hover:bg-gray-200 max-w-[180px] truncate"
            title={section.title}
            onClick={() => document.getElementById(`simon-deck-sec-${i}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })}>
            {i + 1} · {section.title} <span className="text-gray-400">({slides.length})</span>
          </button>
        ))}
        <span className="flex-1" />
        <div className="flex items-center bg-gray-100 rounded-md p-0.5 text-xs">
          {(['copy', 'preview'] as const).map((m) => (
            <button key={m} type="button"
              className={`px-2.5 py-1 rounded transition-all ${mode === m ? 'bg-white shadow-sm text-gray-900 font-medium' : 'text-gray-500 hover:text-gray-700'}`}
              onClick={() => setMode(m)}>
              {m === 'copy' ? 'Copy' : 'Preview'}
            </button>
          ))}
        </div>
      </div>
      <div className="space-y-5">
        {secs.map(({ section, slides }, i) => (
          <details key={i} open id={`simon-deck-sec-${i}`} className="scroll-mt-12">
            <summary className="cursor-pointer select-none">
              <span className="inline-flex items-baseline gap-3">
                <span className="text-sm font-semibold text-gray-800">
                  SECTION {i + 1} — {section.title}
                </span>
                <CharMeter text={section.title} limit={SIMON_LIMITS.title} />
                <span className="text-xs text-gray-400">{slides.length} slides</span>
              </span>
            </summary>
            <div className="mt-2 space-y-2.5">
              {slides.map((s, j) => (
                mode === 'copy'
                  ? <SlideCard key={j} number={`${i + 1}.${j + 1}`} block={s} />
                  : <SlidePreview key={j} number={`${i + 1}.${j + 1}`} block={s} />
              ))}
            </div>
          </details>
        ))}
      </div>
    </DetailPanel>
  );
}

/** Outcome of the targeted CEFR pass: word-level fixes are applied silently;
    anything egregiously off-level is flagged here for a human look. */
export function LevelCheckDetail({ doc }: { doc: DesignDocument | null }) {
  const courseDoc = doc?.realizations?.simon?.course_doc;
  const flags = courseDoc?.level_flags ?? [];
  if (!courseDoc) return <DetailPanel title="CEFR level check">Not run yet.</DetailPanel>;
  return (
    <DetailPanel title="CEFR level check">
      <p className="text-sm text-gray-500 mb-3">
        Drafting writes to the target level; this pass makes targeted word-level
        substitutions and flags slides it can't fix with word swaps.
      </p>
      {flags.length === 0 ? (
        <div className="text-sm text-green-700">✓ No slides flagged — copy is on level.</div>
      ) : (
        <div className="space-y-1.5">
          {flags.map((f, i) => (
            <div key={i} className="flex items-start gap-2 text-sm">
              <Chip tone="amber">slide {f.section}.{f.index}</Chip>
              <span className="text-gray-700">{f.reason}</span>
            </div>
          ))}
        </div>
      )}
    </DetailPanel>
  );
}

export function ValidateDetail({ doc }: { doc: DesignDocument | null }) {
  const validation = doc?.realizations?.simon?.validation;
  const substitutions = doc?.realizations?.simon?.course_doc?.substitutions ?? [];
  return (
    <DetailPanel title="Validation">
      {validation?.validated && (
        <div className="text-sm text-green-700">✓ All Simon limits and structure rules passed.</div>
      )}
      {validation && !validation.validated && (
        <div>
          <div className="text-sm font-medium text-red-700 mb-2">
            {(validation.issues ?? []).length} check(s) failed — fix the design (or use
            “Allow word” for vocabulary flags) and re-render:
          </div>
          <div className="space-y-1 max-h-96 overflow-y-auto">
            {(validation.issues ?? []).map((issue, i) => (
              <div key={i} className="flex items-start gap-2 text-sm">
                {issue.block_index && <Chip tone="red">block {issue.block_index}</Chip>}
                <span className="text-gray-700">{issue.message}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {!validation && <div className="text-sm text-gray-500">Not validated yet.</div>}
      {substitutions.length > 0 && (
        <div className="mt-3">
          <div className="text-sm font-medium text-gray-700 mb-1">
            Design intents with no Simon form (omitted, by design):
          </div>
          <ul className="text-sm text-gray-500 list-disc ml-4">
            {substitutions.map((s) => <li key={s.intent_id}>{s.intent_id} — {s.reason}</li>)}
          </ul>
        </div>
      )}
    </DetailPanel>
  );
}

const FILE_TONES: Record<string, 'green' | 'blue' | 'red' | 'amber' | 'gray'> = {
  generated: 'green', copied: 'green', exists: 'green',
  rendering: 'blue', failed: 'red', pending_heygen: 'amber',
};

export function AssetsDetail({ doc }: { doc: DesignDocument | null }) {
  const assets = doc?.realizations?.simon?.assets;
  if (!assets) return <DetailPanel title="Media files">No media generated yet.</DetailPanel>;
  return (
    <DetailPanel title="Media files">
      <div className="space-y-1">
        {(assets.files ?? []).map((f) => (
          <div key={f.filename} className="flex justify-between items-center text-sm border-b border-gray-50 pb-1">
            <span className="font-mono text-xs text-gray-700">{f.filename}</span>
            <span className="flex items-center gap-1.5">
              {f.qa_warning && <Chip tone="amber">⚠ {f.qa_warning}</Chip>}
              <Chip tone={FILE_TONES[f.status] ?? 'gray'}>{f.status}</Chip>
            </span>
          </div>
        ))}
      </div>
    </DetailPanel>
  );
}

const CHECKLIST: Array<[string, string]> = [
  ['assets_uploaded', 'Assets uploaded to Simon media library'],
  ['built', 'Course built via claude.ai Chrome session'],
  ['verified', 'Independent verification passed'],
  ['published', 'Sent to My Courses / published'],
];

function downloadFile(filename: string, content: string, type: string) {
  const url = URL.createObjectURL(new Blob([content], { type }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function PackageDetail({ run, doc, onRefresh, setError }: {
  run: DesignRun;
  doc: DesignDocument | null;
  onRefresh: () => void;
  setError: (m: string | null) => void;
}) {
  const [fetched, setFetched] = useState<{ build_spec_md: string; course_json: string } | null>(null);
  const [copied, setCopied] = useState(false);
  const [showSpec, setShowSpec] = useState(false);
  const realization = run.realizations?.simon;
  const pkg = doc?.realizations?.simon?.package;
  const assets = doc?.realizations?.simon?.assets;
  const rendering = assets?.rendering_videos?.length ?? 0;
  if (!pkg) return <DetailPanel title="Build Package">Not compiled yet.</DetailPanel>;

  const loadPackage = async () => {
    if (fetched) return fetched;
    const p = await designRunsService.getPackage(run.run_id);
    setFetched(p);
    return p;
  };

  return (
    <DetailPanel title="Build Package">
      <div className="text-sm text-gray-500 mb-3">
        {pkg.assertions.counts.sections} sections · {pkg.assertions.counts.knowledge_checks} knowledge
        checks · {pkg.assertions.counts.questions} questions ·{' '}
        {pkg.asset_manifest.length} media files
      </div>
      <div className="flex gap-2 flex-wrap items-center mb-2">
        <button className="px-3 py-1.5 rounded-md text-sm text-white hover:opacity-90"
          style={{ backgroundColor: copied ? '#16a34a' : NAVY }}
          onClick={async () => {
            try {
              const p = await loadPackage();
              await navigator.clipboard.writeText(p.build_spec_md);
              setCopied(true);
              setTimeout(() => setCopied(false), 2500);
            } catch (e: unknown) { setError(e instanceof Error ? e.message : String(e)); }
          }}>
          {copied ? '✓ Copied — paste into the claude.ai prompt box' : '📋 Copy build prompt'}
        </button>
        <button className="px-3 py-1.5 rounded-md text-sm text-white hover:opacity-90"
          style={{ backgroundColor: NAVY }}
          onClick={async () => {
            try {
              if (!assets?.zip_key) throw new Error('No assets.zip yet');
              window.open(await getPresignedUrl(assets.zip_key), '_blank');
            } catch (e: unknown) { setError(e instanceof Error ? e.message : String(e)); }
          }}>
          ⬇ assets.zip{rendering ? ' (videos still rendering)' : ''}
        </button>
      </div>
      <div className="flex gap-3 mb-4 text-xs">
        <button className="text-gray-400 hover:text-gray-600"
          onClick={async () => {
            try {
              await loadPackage();
              setShowSpec((v) => !v);
            } catch (e: unknown) { setError(e instanceof Error ? e.message : String(e)); }
          }}>
          {showSpec ? 'Hide build prompt' : 'View build prompt'}
        </button>
        <button className="text-gray-400 hover:text-gray-600"
          onClick={async () => {
            try {
              const p = await loadPackage();
              downloadFile('build-spec.md', p.build_spec_md, 'text/markdown');
              downloadFile('simon-course.json', p.course_json, 'application/json');
            } catch (e: unknown) { setError(e instanceof Error ? e.message : String(e)); }
          }}>
          ⬇ Download as files (.md + .json)
        </button>
      </div>
      {showSpec && fetched && (
        <pre className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-md text-xs text-gray-700 whitespace-pre-wrap max-h-96 overflow-y-auto">
          {fetched.build_spec_md}
        </pre>
      )}
      <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Manual steps</div>
      <div className="space-y-1.5">
        {CHECKLIST.map(([key, label]) => {
          const done = Boolean(realization?.checklist?.[key]);
          return (
            <label key={key}
              className={`flex items-center gap-2 text-sm cursor-pointer ${done ? 'text-gray-400 line-through' : 'text-gray-700'}`}>
              <input type="checkbox" checked={done}
                onChange={async (e) => {
                  await designRunsService.setChecklist(run.run_id, 'simon', key, e.target.checked);
                  onRefresh();
                }} />
              {label}
            </label>
          );
        })}
      </div>
    </DetailPanel>
  );
}

export function VideosDetail({ doc }: { doc: DesignDocument | null }) {
  const assets = doc?.realizations?.simon?.assets;
  const videos = (assets?.files ?? []).filter((f) => f.filename.endsWith('.mp4'));
  if (!videos.length) return <DetailPanel title="Scenario videos">No videos in this course.</DetailPanel>;
  return (
    <DetailPanel title="Scenario videos (HeyGen avatar renders)">
      <div className="space-y-1">
        {videos.map((v) => (
          <div key={v.filename} className="flex justify-between items-center text-sm border-b border-gray-50 pb-1">
            <span className="font-mono text-xs text-gray-700">{v.filename}</span>
            <span className="flex items-center gap-1.5">
              {v.qa_warning && <span className="text-xs text-red-600">{v.qa_warning}</span>}
              <Chip tone={FILE_TONES[v.status] ?? 'gray'}>{v.status}</Chip>
            </span>
          </div>
        ))}
      </div>
      {(assets?.rendering_videos?.length ?? 0) > 0 && (
        <div className="text-xs text-gray-400 mt-2">
          Rendering continues in the background — the zip updates itself when each video lands.
        </div>
      )}
    </DetailPanel>
  );
}
